import type {
  MemoryTimelineResponse,
  MemoryAgentResponse,
  MemoryStaleResponse,
} from '../types/memory.js';

const BASE = '/api/memory-bus';
const MEM = '/api/memory';

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error?.message ?? body.error ?? `Request failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

async function postJson<T>(url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: body != null ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((data as { error?: string }).error ?? `Request failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export function getTimeline(memoryRef: string, limit = 50): Promise<MemoryTimelineResponse> {
  const params = new URLSearchParams({ ref: memoryRef, limit: String(limit) });
  return fetchJson<MemoryTimelineResponse>(`${BASE}/timeline?${params}`);
}

export function getAgentMemories(
  agentId: string,
  opts: { since?: string; limit?: number } = {},
): Promise<MemoryAgentResponse> {
  const params = new URLSearchParams();
  if (opts.since) params.set('since', opts.since);
  if (opts.limit) params.set('limit', String(opts.limit));
  const qs = params.toString();
  return fetchJson<MemoryAgentResponse>(
    `${BASE}/agent/${encodeURIComponent(agentId)}${qs ? `?${qs}` : ''}`,
  );
}

export function getStaleMemories(days = 90): Promise<MemoryStaleResponse> {
  return fetchJson<MemoryStaleResponse>(`${BASE}/stale?days=${days}`);
}

// --- New endpoints (MEMC-070) ---

export interface MemoryGap {
  id: number;
  agent_id: number | null;
  domain_keyword: string;
  count: number;
  first_seen_at: string;
  last_seen_at: string;
  status: string;
}

export interface ConversationMemoryEntry {
  id: number;
  agent_id: number;
  user_id: number;
  conversation_id: number | null;
  summary: string;
  message_count: number;
  last_activity_at: string | null;
  tokens_estimate: number | null;
  updated_at: string;
}

export interface PromotionCandidate {
  id: number;
  source_agent_id: number;
  pattern_tag: string;
  cluster_count: number;
  proposed_skill_title: string | null;
  status: string;
  curator_decision_reason: string | null;
  created_at: string;
}

export function getMemoryGaps(
  status = 'open',
  limit = 50,
): Promise<{ status: string; gaps: MemoryGap[] }> {
  const params = new URLSearchParams({ status, limit: String(limit) });
  return fetchJson(`${MEM}/gaps?${params}`);
}

export function getConversationMemory(
  agentId: number,
  userId: number,
): Promise<{ agentId: number; userId: number; entries: ConversationMemoryEntry[] }> {
  return fetchJson(`${MEM}/conversation/${agentId}/${userId}`);
}

export function getPromotionCandidates(
  status = 'curator_approved',
  limit = 50,
): Promise<{ status: string; candidates: PromotionCandidate[] }> {
  const params = new URLSearchParams({ status, limit: String(limit) });
  return fetchJson(`${MEM}/promotion-candidates?${params}`);
}

export function verifyConfidence(
  id: number,
  confidence: 'low' | 'medium' | 'high',
): Promise<{ success: boolean; id: number; confidence: string; last_verified_at: string }> {
  return postJson(`${MEM}/${id}/verify`, { confidence });
}
